import { useEffect } from "react";

interface FeedbucketAppProps {
  feedbucketKey: string | null;
  name?: string | null;
  email?: string | null;
  token: string | null;
  activate?: boolean;
}

const FeedbucketLoader: React.FC<FeedbucketAppProps> = ({
  name,
  email,
  token,
  feedbucketKey,
  activate,
}) => {
  /**
   * Function used to give style to the feedbucket app
   */
  const styling = () => {
    var sheet = new CSSStyleSheet();
    // Override styling for the widget bar and the bar's buttons using their tailwind classes
    // First move the widget to bottom-right corner, make it horizontal, then correct the button orientation
    sheet.replaceSync(`
      :host {
        --feedbucket-primary-color: var(--color-secondary-600) !important;
      }
    `);
    
    const feedbucketApp: any = document.querySelector("feedbucket-app");
    if (feedbucketApp && feedbucketApp.shadowRoot) {
      const shadowRoot = feedbucketApp.shadowRoot;
      shadowRoot.adoptedStyleSheets.push(sheet);
    }
  };

  const removeElement = (element: Element | null) => {
    // Clear config
    delete (window as any).feedbucketConfig;
    // And try to remove it from DOM
    if (element) (element as any).style.visibility = "hidden";
    return;
  }

  useEffect(() => {
    // Try to get the feedbucket app
    const feedbucketApp: any = document.querySelector("feedbucket-app");

    // Check if the feedbcuket key is defined
    if (!feedbucketKey) return removeElement(feedbucketApp);

    // Check if the token is defined
    if (!token) return removeElement(feedbucketApp);

    // Define the feedbucket config
    (window as any).feedbucketConfig = {
      reporter: { name, email, token },
    };

    // If it's needed to deactivate
    if (!activate) return removeElement(feedbucketApp);

    // Check if it's already defined
    if (feedbucketApp) {
      // And try to remove it from DOM
      feedbucketApp.style.visibility = "visible";
      return;
    }

    // Otherwise, just load the content
    const script: HTMLScriptElement = document.createElement("script");
    script.type = "module";
    script.async = true;
    script.src = "https://cdn.feedbucket.app/assets/feedbucket.js";
    script.dataset.feedbucket = feedbucketKey;
    document.head.appendChild(script);
    // After loading, apply the styling
    script.onload = () => styling();

    return () => {
      // Remove script
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }

      // Remove widget if Feedbucket inserted one
      const widget = document.querySelector("[data-feedbucket-root]");
      if (widget && widget.parentNode) {
        widget.parentNode.removeChild(widget);
      }

      // Clear config
      delete (window as any).feedbucketConfig;
    };
  }, [name, email, token, feedbucketKey, activate]);

  return null;
};

export default FeedbucketLoader;
